import {
  type FetchResult,
  createRetryMiddleware,
  createYaAuthMiddleware,
  fetchWithHeaders,
  withCSRF,
  withIdempotency,
  withRequestHasBeenTerminatedRetry,
  withStartup,
  withTimezone,
} from '@yandex-delivery/core/utils/network'
import type {TypedRequestConfig} from '@yandex-delivery/core/utils/network/types'
import {flow} from '@yandex-delivery/utils'
import {config} from 'src/shared/config'
import i18n from 'src/shared/i18n'

import {withNoAuthSharedRouteRedirect} from './middleware/withNoAuthSharedRouteRedirect'
import {withStartupUserId} from './middleware/withStartupUserId'

export type YaAuthFetchConfig = TypedRequestConfig & {
  idempotent?: boolean
  retries?: number
}

export type YaAuthFetch = <TResponse = unknown>(config: YaAuthFetchConfig) => Promise<FetchResult<TResponse>>

const baseHeaders: Record<string, string> = {
  'Content-Type': 'application/json',
  'Accept-Language': config.lang,
  'X-Taxi': `${navigator.userAgent} turboapp_delivery`,
}

if (config.token) {
  baseHeaders.Authorization = `Bearer ${config.token}`
}

const withYaAuth = createYaAuthMiddleware({
  host: config.yaAuthProxy.host,
  headers: baseHeaders,
})

const withRetry = createRetryMiddleware({retries: 2, delay: 1000})
const withRequestRetry = withRequestHasBeenTerminatedRetry({
  customErrorMessage: i18n.print('request.retry.error'),
})

if (process.env.NODE_ENV !== 'production') {
  // biome-ignore lint/suspicious/noConsole: отладочный вывод только вне production
  console.log('[ya-auth-fetch] Auth middleware selection:', {
    apiMode: config.apiMode,
    hasToken: Boolean(config.token),
    hasUserId: Boolean(config.userId),
    middleware:
      config.apiMode === 'auth'
        ? config.userId
          ? 'withStartupUserId (optimistic, with fallback)'
          : 'withStartup (direct)'
        : 'withStartup',
  })
}

const baseFetch = flow(
  withTimezone,
  withYaAuth,
  withCSRF,
  config.apiMode === 'auth' ? withStartupUserId : withStartup,
  withIdempotency,
  withRequestRetry,
  withRetry,
)(fetchWithHeaders) as YaAuthFetch

export const yaAuthFetch: YaAuthFetch = config.apiMode === 'auth' ? withNoAuthSharedRouteRedirect(baseFetch) : baseFetch
